import {
  DEFAULT_REMOTE_AUDIO_MONETIZATION_POLICY,
  type SubscriptionTier,
} from "./audioMonetizationPolicy";

export type InterstitialBreakEvent =
  | "practiceHeadingComplete"
  | "vocabularySessionComplete"
  | "scenarioExit"
  | "missionResult";

export interface EvaluateInterstitialBreakInput {
  subscriptionTier: SubscriptionTier;
  adsEnabled?: boolean;
  event: InterstitialBreakEvent;
  nowMs?: number;
  lastInterstitialAtMs?: number | null;
  lastRewardedUnlockAtMs?: number | null;
  cooldownMs?: number;
}

export interface InterstitialBreakDecision {
  shouldShow: boolean;
  reason: "ok" | "premium" | "ads_disabled" | "cooldown" | "recent_rewarded";
  nextEligibleAtMs: number | null;
}

export function evaluateInterstitialBreak(
  input: EvaluateInterstitialBreakInput,
): InterstitialBreakDecision {
  const {
    subscriptionTier,
    adsEnabled = true,
    nowMs = Date.now(),
    lastInterstitialAtMs = null,
    lastRewardedUnlockAtMs = null,
    cooldownMs = DEFAULT_REMOTE_AUDIO_MONETIZATION_POLICY.interstitialCooldownMs,
  } = input;

  if (subscriptionTier === "premium") {
    return {
      shouldShow: false,
      reason: "premium",
      nextEligibleAtMs: null,
    };
  }

  if (!adsEnabled) {
    return {
      shouldShow: false,
      reason: "ads_disabled",
      nextEligibleAtMs: null,
    };
  }

  if (Number.isFinite(lastRewardedUnlockAtMs) && lastRewardedUnlockAtMs !== null) {
    const nextEligibleAtMs = lastRewardedUnlockAtMs + cooldownMs;
    if (nextEligibleAtMs > nowMs) {
      return {
        shouldShow: false,
        reason: "recent_rewarded",
        nextEligibleAtMs,
      };
    }
  }

  if (Number.isFinite(lastInterstitialAtMs) && lastInterstitialAtMs !== null) {
    const nextEligibleAtMs = lastInterstitialAtMs + cooldownMs;
    if (nextEligibleAtMs > nowMs) {
      return {
        shouldShow: false,
        reason: "cooldown",
        nextEligibleAtMs,
      };
    }
  }

  return {
    shouldShow: true,
    reason: "ok",
    nextEligibleAtMs: null,
  };
}
